import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {isUndefined} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';

import {SkeletonLoader} from '~/shared/components/Loaders';
import {scrollWindowTop} from '~/shared/utils/general';
import PageHeader from '~/common/components/PageHeader';
import actions from '~/shared/store/actions';
import {getCurrentLocation} from '~/shared/router';
import {DeliveryTrackerJobStatuses, DELIVERY_INTERVAL_TIME} from '~/shared/consts/deliveryTrackerConsts';
import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {
  selectIsScooberJobCanceled,
  selectIsTrackerUrlEmpty,
  selectOrderSuccessData,
  selectUserLastOrderScooberJob,
  selectIsOrderBarcodeEnable,
  selectIsBenefitsHighlightEnabled,
  selectOrderSuccessBillingLinesWithDiscounts,
} from '~/shared/store/selectors';
import {createLogger} from '~/shared/logging';
import {navigateToDefaultStartPage} from '~/shared/services/navigation';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {Payments, BarcodeOrderIdArea} from '~/shared/components/OrderCompleteSections';
import {useIsIOS} from '~/shared/hooks/deviceInfo';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {isDonationRestaurant} from '~/shared/utils/donationRestaurants';
import {handleRefreshToken} from '~/shared/services/auth';
import {RestaurantBusinessTypeName} from '~/shared/store/models';
import RestaurantMultipassCard from '~/common/components/RestaurantMultipassCard';
import {Section} from '~/shared/components/OrderCompleteSections/parts';
import {is401Error} from '~/shared/services/apiErrorService';
import {getTotalSavedAmountFromOrderSuccessBillingLines} from '~/shared/utils/billingLinesCalculation';
import {OrderState} from '~/shared/store/storeModules/order/orderReducer';
import {orderSuccessContainerSpacing} from '~/shared/components/styled';

import useIsShowMap from './hooks/useIsShowMap';
import {BarcodeOrDetailsBlock} from './BarcodeOrDetailsBlock';
import {BottomBlock} from './BottomBlock';
import {MapAndOrderBlock} from './MapAndOrderBlock';
import OrderSuccessLoader from './OrderSuccessLoader';
import OrderSuccessViewFooter from './OrderSuccessViewFooter';

const logger = createLogger('OrderSuccess');

const MOBILE_FOOTER = 66;
const DESKTOP_FOOTER = 170;

const BackgroundBoxOverIframeWidth = 274;
const BackgroundBoxOverIframeColor = '#f8f5f2';

const Root = styled.div<{isIos?: boolean}>`
  ${orderSuccessContainerSpacing}
  margin-top: ${({theme}) => theme.newHeader.height.mobile}px;
  ${media.minLargeMobile`
    margin-top: ${({theme}) => theme.newHeader.height.rest}px;
  `}

  padding: 8px 2px;
  min-height: calc(100vh - ${({theme}) => theme.header.height + DESKTOP_FOOTER}px);
  ${flexColumn}
  justify-content: flex-start;
  ${({isIos}) => !isIos && 'overflow-x: hidden;'}
  ${media.minMobile`
    min-height: calc(100vh - ${({theme}) => theme.header.height + MOBILE_FOOTER}px);
  `}
`;

const BackgroundBoxOverIframeLeft = styled.div`
  ${media.minLargeTablet`
      position: absolute;
      width: calc(50% - ${BackgroundBoxOverIframeWidth}px);
      height: 144px;
      top: 450px;
      left: 0;
      background-color: ${BackgroundBoxOverIframeColor};
      z-index: 10;
  `}
`;

const BackgroundBoxOverIframeRight = styled.div`
  ${media.minLargeTablet`
    position: absolute;
    width: calc(50% - ${BackgroundBoxOverIframeWidth}px);
    height: 144px;
    top: 450px;
    right: -2px;
    background-color: ${BackgroundBoxOverIframeColor};
    z-index: 10;
  `}
`;

const RestaurantMultipassCardWrapper = styled(Section)`
  padding: 18px 24px;
  margin-top: 8px;
  ${flexColumn}
`;

const useShouldShowOdDummyTacker = (orderSuccessData?: OrderState['successData']['data']) => {
  if (!orderSuccessData) {
    return;
  }

  return !orderSuccessData.restaurant?.isScoober &&
    orderSuccessData.deliveryMethod?.toLowerCase() === DeliveryMethods.DELIVERY &&
    !orderSuccessData.isFutureOrder &&
    !orderSuccessData.isBarcodeOrder &&
    !orderSuccessData.isPoolOrder;
};

const OrderSuccessView = ({allowInit}: {
  allowInit?: boolean;
}) => {
  const [scooberJobFetchFinished, setScooberJobFetchFinished] = useState(false);
  const isIos = useIsIOS();
  const {routeParams} = getCurrentLocation();
  const dispatch = useDispatch();
  const scooberOrderInterval = useRef<ReturnType<typeof setTimeout> | undefined>();

  const orderSuccessData = useSelector(selectOrderSuccessData);
  const isStatusCanceled = useSelector(selectIsScooberJobCanceled);
  const isTrackerUrlEmpty = useSelector(selectIsTrackerUrlEmpty);
  const isOrderBarcodeEnable = useSelector(selectIsOrderBarcodeEnable);
  const isBenefitsHighlightEnabled = useSelector(selectIsBenefitsHighlightEnabled);
  const orderSuccessBillingLines = useSelector(selectOrderSuccessBillingLinesWithDiscounts);

  const totalSavedAmount = useMemo(() => {
    if (!orderSuccessData) {
      return 0;
    }

    return getTotalSavedAmountFromOrderSuccessBillingLines(orderSuccessData);
  }, [orderSuccessData]);

  const {trackerUrl, status} = useSelector(selectUserLastOrderScooberJob) || EMPTY_OBJECT;

  const {orderId: orderIdFromRouteParams} = routeParams;

  const {address, restaurant, dishToPresent, orderStatus, deliveryMethod} = orderSuccessData || {};

  const isScoober = restaurant?.isScoober;
  const isVoucherCard = restaurant?.businessType === RestaurantBusinessTypeName.VoucherCard;
  const isShowMap = useIsShowMap();
  
  const isShowBackgroundBoxIframeDivs = trackerUrl && !isTrackerUrlEmpty && !isShowMap;
  const isEnlargeTopMargin = !!trackerUrl && !isTrackerUrlEmpty && !isStatusCanceled && isScoober;

  const fetchScooberData = useCallback(async () => {
    try {
      await dispatch(actions.getUserLastOrderScooberJob(orderIdFromRouteParams));
    } catch (error) {
      logger.error('Error while trying to getUserLastOrderScooberJob', {error});
      if (is401Error(error)) {
        await handleRefreshToken(error, fetchScooberData);
        return;
      }
      navigateToDefaultStartPage();
    } finally {
      setScooberJobFetchFinished(true);
      const clearScooberInterval = status === DeliveryTrackerJobStatuses.EatingTime;

      if (clearScooberInterval) {
        clearInterval(scooberOrderInterval.current);
      }
    }
  }, [dispatch, orderIdFromRouteParams, status, setScooberJobFetchFinished]);

  const shouldFetchScooberData = restaurant?.isScoober;

  useEffect(() => {
    scrollWindowTop({disabledAnimation: true});
    if (shouldFetchScooberData) {
      scooberOrderInterval.current = setInterval(fetchScooberData, DELIVERY_INTERVAL_TIME);
    }
    return () => clearInterval(scooberOrderInterval.current);
  }, [shouldFetchScooberData, fetchScooberData]);

  useEffect(() => {
    if (shouldFetchScooberData && allowInit) {
      fetchScooberData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchScooberData, allowInit]);

  const shouldShowOdDummyTracker = useShouldShowOdDummyTacker(orderSuccessData);
  const shouldShowLoader = isUndefined(shouldShowOdDummyTracker) || isUndefined(orderSuccessData) || isUndefined(address) || isUndefined(restaurant) || (isScoober && !scooberJobFetchFinished);
  const showAgeRestrictionDisclaimer = !!dishToPresent?.some(({ageRestricted}) => ageRestricted);

  const isDonation = restaurant && isDonationRestaurant(restaurant.restaurantId);
  const barcodeOrderId = (deliveryMethod?.toLowerCase() === DeliveryMethods.PICKUP && isOrderBarcodeEnable && orderStatus !== 'Canceled') ? orderIdFromRouteParams : undefined;

  return (
    <Root isIos={isIos}>
      {isShowBackgroundBoxIframeDivs && (
        <>
          <BackgroundBoxOverIframeLeft />
          <BackgroundBoxOverIframeRight />
        </>
      )}
      <PageHeader />

      <SkeletonLoader shouldShowLoader={shouldShowLoader} LoaderComponent={OrderSuccessLoader}>
        {orderSuccessData && (
          <>
            <MapAndOrderBlock
              shouldShowOdDummyTracker={shouldShowOdDummyTracker}
              isDonation={isDonation}
              isVoucherCard={isVoucherCard}
            />
            {!isVoucherCard && (
              <>
                {barcodeOrderId && <BarcodeOrderIdArea barcodeOrderId={barcodeOrderId} />}
                <BarcodeOrDetailsBlock isDonation={isDonation} />
              </>
            )}
            {isVoucherCard && (
              <RestaurantMultipassCardWrapper>
                <RestaurantMultipassCard allowInit={allowInit} orderData={orderSuccessData}/>
              </RestaurantMultipassCardWrapper>
            )}
            <Payments
              showAgeRestrictionDisclaimer={showAgeRestrictionDisclaimer}
              billingLines={orderSuccessBillingLines}
              payments={orderSuccessData.orderPayments}
              loyaltyProgramPoints={orderSuccessData?.numOfLoyaltyProgramPointsEarned}
              isBarCodeOrder={Boolean(barcodeOrderId)}
              isVoucherCard={isVoucherCard}
              totalSaved={totalSavedAmount}
              isBenefitsHighlightEnabled={isBenefitsHighlightEnabled}
              orderCoupon={orderSuccessData.orderCoupon}
            />
            <BottomBlock isDonation={isDonation} />
          </>
        )}
      </SkeletonLoader>
      <OrderSuccessViewFooter isEnlargeTopMargin={isEnlargeTopMargin} />
    </Root>
  );
};

export default OrderSuccessView;
